import { createFileRoute } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { FileText, FolderPlus, Folder as FolderIcon, Trash2, History, Eye, AlertTriangle } from 'lucide-react'
import { toast } from 'sonner'
import { useAuth } from '@/hooks/use-auth'
import { useTeam } from '@/hooks/use-company'
import {
  useDocuments, useDocumentViews, openDocument,
  type CompanyDocument, type DocumentFolder, type DocumentStatus,
} from '@/hooks/use-documents'
import { DocumentUpload } from '@/components/document-upload'
import {
  Page, PageHeader, StatusPill, SearchInput, SkeletonRows, EmptyState,
  Workspace, btnPrimary, btnSecondary, btnGhost,
} from '@/components/rm'
import { Modal, Field, Input, Select } from '@/components/rm/form'
import { cn } from '@/lib/utils'
import { phFormat } from '@/lib/ph'

export const Route = createFileRoute('/_app/documents')({ component: DocumentsPage })

const MAX_DEPTH = 3
const mb = (bytes: number) => `${(bytes / 1048576).toFixed(bytes < 1048576 ? 2 : 1)} MB`
const day = (iso: string | null) => {
  if (!iso) return '—'
  const d = new Date(iso)
  return isNaN(d.getTime()) ? '—' : phFormat(d, 'date', { day: 'numeric', month: 'short', year: 'numeric' })
}
const stamp = (iso: string) => {
  const d = new Date(iso)
  return isNaN(d.getTime()) ? '—' : phFormat(d, 'datetime', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })
}
const isOverdue = (reviewBy: string | null) => !!reviewBy && new Date(reviewBy) < new Date()

/** Depth of a folder in the tree, used to stop the UI nesting past MAX_DEPTH. */
function depthOf(id: string | null, byId: Map<string, DocumentFolder>): number {
  let d = 0
  let cur = id ? byId.get(id) ?? null : null
  while (cur) { d += 1; cur = cur.parent_id ? byId.get(cur.parent_id) ?? null : null }
  return d
}

/**
 * "contents not indexed" is shown wherever a document appears, not only in search results. A
 * worker who searches a scan and finds nothing has to be able to tell that the body was never
 * read, rather than concluding that no such procedure exists.
 */
function ExtractNote({ status }: { status: CompanyDocument['extract_status'] }) {
  if (status === 'done') return null
  const text = status === 'no_text_layer' ? 'Contents not indexed'
    : status === 'failed' ? 'Could not read contents'
    : 'Indexing…'
  return (
    <span className="inline-flex items-center gap-1 rounded-[6px] bg-token-hover px-1.5 py-0.5 text-[10.5px] font-semibold text-token-secondary">
      {status === 'no_text_layer' && <AlertTriangle className="h-3 w-3" />} {text}
    </span>
  )
}

function DocumentsPage() {
  const { role } = useAuth()
  const isAdmin = role === 'company-admin'
  const { members } = useTeam()

  const [folderId, setFolderId] = useState<string | null | undefined>(undefined)
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<CompanyDocument | null>(null)
  const [newFolderOpen, setNewFolderOpen] = useState(false)
  const [folderName, setFolderName] = useState('')
  const [uploadOpen, setUploadOpen] = useState(false)
  const [title, setTitle] = useState('')
  const [pendingFile, setPendingFile] = useState<File | null>(null)
  const [replacing, setReplacing] = useState<CompanyDocument | null>(null)
  const [historyFor, setHistoryFor] = useState<CompanyDocument | null>(null)

  const {
    folders, documents, results,
    useVersions, createFolder, deleteFolder, upload, update, remove,
  } = useDocuments({ folderId, search })

  const versions = useVersions(historyFor?.id ?? null)
  const views = useDocumentViews(selected?.id ?? null)

  const byId = useMemo(() => new Map((folders.data ?? []).map((f) => [f.id, f])), [folders.data])
  const roots = useMemo(() => (folders.data ?? []).filter((f) => !f.parent_id), [folders.data])
  const childrenOf = (id: string) => (folders.data ?? []).filter((f) => f.parent_id === id)

  const searching = search.trim().length > 1
  const rows = documents.data ?? []
  const hits = results.data ?? []

  const userName = (id: string) => {
    const m = members.find((x) => x.id === id)
    return m ? [m.first_name, m.last_name].filter(Boolean).join(' ').trim() || m.email || 'Someone' : 'Someone'
  }

  const doUpload = async () => {
    if (!pendingFile) return toast.error('Choose a file first.')
    try {
      await upload.mutateAsync({
        file: pendingFile,
        title: title.trim() || pendingFile.name,
        folderId: replacing ? replacing.folder_id : (folderId ?? null),
        supersedes: replacing ? { id: replacing.id, version: replacing.version } : null,
      })
      toast.success(replacing ? `Version ${replacing.version + 1} uploaded` : 'Document uploaded')
      setUploadOpen(false); setPendingFile(null); setTitle(''); setReplacing(null)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Upload failed')
    }
  }

  const open = async (doc: CompanyDocument) => {
    try {
      const url = await openDocument(doc)
      window.open(url, '_blank', 'noopener')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not open that document')
    }
  }

  const setStatus = async (doc: CompanyDocument, status: DocumentStatus) => {
    try {
      await update.mutateAsync({ id: doc.id, status })
      toast.success(status === 'published' ? 'Published — crew can see it now' : `Marked ${status}`)
      if (selected?.id === doc.id) setSelected({ ...doc, status })
    } catch (e) { toast.error(e instanceof Error ? e.message : 'Failed') }
  }

  const folderNode = (f: DocumentFolder, depth: number) => (
    <div key={f.id}>
      <button
        onClick={() => setFolderId(f.id)}
        aria-current={folderId === f.id ? 'page' : undefined}
        className={cn(
          'relative flex w-full items-center gap-2 px-4 py-2 text-left text-[13px] transition-colors',
          folderId === f.id ? 'bg-token-selected text-token-primary' : 'text-token-secondary hover:bg-token-hover',
        )}
        style={{ paddingLeft: 16 + depth * 14 }}
      >
        {folderId === f.id && <span className="absolute inset-y-0 left-0 w-[3px] bg-brand" />}
        <FolderIcon className="h-4 w-4 flex-none" />
        <span className="min-w-0 flex-1 truncate">{f.name}</span>
        {isAdmin && (
          <span
            role="button"
            tabIndex={0}
            aria-label={`Delete folder ${f.name}`}
            className={btnGhost + ' !h-6 !px-1'}
            onClick={async (e) => {
              e.stopPropagation()
              if (!confirm(`Delete "${f.name}"? Its documents are kept and become unfiled.`)) return
              try { await deleteFolder.mutateAsync(f.id); toast.success('Folder deleted, documents kept') }
              catch (err) { toast.error(err instanceof Error ? err.message : 'Failed') }
            }}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </span>
        )}
      </button>
      {depth + 1 < MAX_DEPTH && childrenOf(f.id).map((c) => folderNode(c, depth + 1))}
    </div>
  )

  return (
    <Page>
      <PageHeader
        title="Documents"
        subtitle="Procedures, plans and reference material for the crew."
        actions={isAdmin ? (
          <>
            <button className={btnSecondary} onClick={() => setNewFolderOpen(true)}><FolderPlus className="h-4 w-4" /> New folder</button>
            <button className={btnPrimary} onClick={() => { setReplacing(null); setUploadOpen(true) }}><FileText className="h-4 w-4" /> Upload</button>
          </>
        ) : undefined}
      />

      <div className="mb-4 mt-5 flex flex-wrap items-center gap-3">
        <SearchInput value={search} onChange={setSearch} placeholder="Search titles, keywords and contents…" />
        {searching && (
          <span className="text-[12.5px] text-token-secondary">
            {results.isLoading ? 'Searching…' : `${hits.length} result${hits.length === 1 ? '' : 's'}`}
          </span>
        )}
      </div>

      <Workspace
        list={
          <div className="flex h-full flex-col">
            <div className="border-b border-token-subtle px-4 py-3">
              <p className="text-[13px] font-semibold text-token-primary">Folders</p>
            </div>
            <div className="divide-y divide-token-subtle">
              <button
                onClick={() => setFolderId(undefined)}
                aria-current={folderId === undefined ? 'page' : undefined}
                className={cn('relative flex w-full items-center gap-2 px-4 py-2 text-left text-[13px] transition-colors',
                  folderId === undefined ? 'bg-token-selected text-token-primary' : 'text-token-secondary hover:bg-token-hover')}
              >
                {folderId === undefined && <span className="absolute inset-y-0 left-0 w-[3px] bg-brand" />}
                <FileText className="h-4 w-4" /> All documents
              </button>
              <button
                onClick={() => setFolderId(null)}
                aria-current={folderId === null ? 'page' : undefined}
                className={cn('relative flex w-full items-center gap-2 px-4 py-2 text-left text-[13px] transition-colors',
                  folderId === null ? 'bg-token-selected text-token-primary' : 'text-token-secondary hover:bg-token-hover')}
              >
                {folderId === null && <span className="absolute inset-y-0 left-0 w-[3px] bg-brand" />}
                <FolderIcon className="h-4 w-4" /> Unfiled
              </button>
              {folders.isLoading ? <SkeletonRows count={4} /> : roots.map((f) => folderNode(f, 0))}
            </div>
          </div>
        }
        inspector={
          <div className="flex h-full flex-col">
            {documents.isLoading && !searching ? (
              <div className="px-6 py-5"><SkeletonRows count={7} /></div>
            ) : searching ? (
              hits.length === 0 && !results.isLoading ? (
                <EmptyState label={`Nothing matches "${search.trim()}".`} />
              ) : (
                <ul className="divide-y divide-token-subtle">
                  {hits.map((h) => (
                    <li key={h.id} className="px-6 py-3.5">
                      <button className="block w-full text-left" onClick={() => {
                        const full = rows.find((r) => r.id === h.id)
                        if (full) { setSelected(full); void open(full) }
                        else void open({ id: h.id, storage_path: '' } as CompanyDocument)
                      }}>
                        <span className="flex flex-wrap items-center gap-2">
                          <span className="text-[14px] font-semibold text-token-primary"
                                dangerouslySetInnerHTML={{ __html: h.snippet || h.title }} />
                          <StatusPill status={h.status} />
                          <ExtractNote status={h.extract_status} />
                        </span>
                        <span className="mt-0.5 block text-[12px] text-token-secondary">
                          {h.folder_name ?? 'Unfiled'} · v{h.version} · {mb(h.size_bytes)}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )
            ) : rows.length === 0 ? (
              <EmptyState
                label="No documents here yet."
                cta={isAdmin ? 'Upload one' : undefined}
                onCta={() => { setReplacing(null); setUploadOpen(true) }}
              />
            ) : (
              <ul className="divide-y divide-token-subtle">
                {rows.map((d) => (
                  <li key={d.id} className={cn('px-6 py-3.5', selected?.id === d.id && 'bg-token-selected')}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <button className="min-w-0 flex-1 text-left" onClick={() => { setSelected(d); void open(d) }}>
                        <span className="flex flex-wrap items-center gap-2">
                          <span className="truncate text-[14px] font-semibold text-token-primary">{d.title}</span>
                          <StatusPill status={d.status} />
                          <ExtractNote status={d.extract_status} />
                          {isOverdue(d.review_by) && (
                            <span className="inline-flex items-center gap-1 rounded-[6px] bg-token-danger-surface px-1.5 py-0.5 text-[10.5px] font-semibold text-token-danger">
                              <AlertTriangle className="h-3 w-3" /> Review due {day(d.review_by)}
                            </span>
                          )}
                        </span>
                        <span className="mt-0.5 block text-[12px] text-token-secondary">
                          v{d.version} · {mb(d.size_bytes)} · {d.offline ? 'Available offline' : 'Online only'} · added {day(d.created_at)}
                        </span>
                        {d.keywords.length > 0 && (
                          <span className="mt-1 block truncate text-[11.5px] text-token-muted">Keywords: {d.keywords.join(', ')}</span>
                        )}
                      </button>
                      {isAdmin && (
                        <div className="flex flex-none flex-wrap items-center gap-1.5">
                          {d.status !== 'published'
                            ? <button className={btnSecondary + ' !h-8'} onClick={() => setStatus(d, 'published')}>Publish</button>
                            : <button className={btnSecondary + ' !h-8'} onClick={() => setStatus(d, 'draft')}>Unpublish</button>}
                          <button className={btnSecondary + ' !h-8'} onClick={() => { setReplacing(d); setTitle(d.title); setUploadOpen(true) }}>New version</button>
                          <button className={btnGhost} aria-label="Version history" onClick={() => setHistoryFor(d)}><History className="h-4 w-4" /></button>
                          <button className={btnGhost} aria-label="Who has read this" onClick={() => setSelected(d)}><Eye className="h-4 w-4" /></button>
                          <button className={btnGhost} aria-label="Delete" onClick={async () => {
                            if (!confirm(`Delete "${d.title}"?`)) return
                            try { await remove.mutateAsync(d.id); toast.success('Deleted') }
                            catch (e) { toast.error(e instanceof Error ? e.message : 'Failed') }
                          }}><Trash2 className="h-4 w-4" /></button>
                        </div>
                      )}
                    </div>

                    {isAdmin && selected?.id === d.id && (
                      <div className="mt-3 rounded-[10px] border border-token-subtle bg-token-surface p-3">
                        <p className="mb-2 text-[12px] font-semibold text-token-primary">Who has opened v{d.version}</p>
                        {views.isLoading ? <SkeletonRows count={2} />
                          : (views.data ?? []).length === 0 ? <p className="text-[12.5px] text-token-secondary">Nobody yet.</p>
                          : (
                            <ul className="space-y-1">
                              {(views.data ?? []).slice(0, 20).map((v) => (
                                <li key={v.id} className="flex items-center justify-between text-[12.5px]">
                                  <span className="text-token-primary">{userName(v.user_id)}</span>
                                  <span className="text-token-secondary">{stamp(v.viewed_at)}{v.offline ? ' · offline' : ''}</span>
                                </li>
                              ))}
                            </ul>
                          )}
                        <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                          <Field label="Keywords (comma separated)">
                            <Input
                              defaultValue={d.keywords.join(', ')}
                              onBlur={(e) => {
                                const next = e.target.value.split(',').map((s) => s.trim()).filter(Boolean)
                                if (next.join('|') !== d.keywords.join('|')) {
                                  update.mutate({ id: d.id, keywords: next })
                                }
                              }}
                            />
                          </Field>
                          <Field label="Review by">
                            <Input
                              type="date"
                              defaultValue={d.review_by ?? ''}
                              onBlur={(e) => { if ((e.target.value || null) !== d.review_by) update.mutate({ id: d.id, reviewBy: e.target.value || null }) }}
                            />
                          </Field>
                        </div>
                        <label className="mt-2 flex items-center gap-2 text-[12.5px] text-token-secondary">
                          <input
                            type="checkbox"
                            checked={d.offline}
                            onChange={(e) => update.mutate({ id: d.id, offline: e.target.checked })}
                          />
                          Available offline on phones
                        </label>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        }
      />

      {/* New folder */}
      <Modal open={newFolderOpen} onClose={() => setNewFolderOpen(false)} title="New folder"
        footer={
          <>
            <button className={btnSecondary} onClick={() => setNewFolderOpen(false)}>Cancel</button>
            <button className={btnPrimary} disabled={createFolder.isPending} onClick={async () => {
              if (!folderName.trim()) return toast.error('Give the folder a name.')
              const parent = typeof folderId === 'string' ? folderId : null
              if (parent && depthOf(parent, byId) >= MAX_DEPTH) {
                return toast.error(`Folders only go ${MAX_DEPTH} levels deep.`)
              }
              try {
                await createFolder.mutateAsync({ name: folderName, parentId: parent })
                setFolderName(''); setNewFolderOpen(false); toast.success('Folder created')
              } catch (e) {
                toast.error(e instanceof Error && /duplicate|unique/i.test(e.message)
                  ? 'There is already a folder with that name here.'
                  : e instanceof Error ? e.message : 'Failed')
              }
            }}>Create</button>
          </>
        }>
        <Field label="Name"><Input value={folderName} onChange={(e) => setFolderName(e.target.value)} placeholder="First aid" /></Field>
        <p className="mt-2 text-[12px] text-token-secondary">
          {typeof folderId === 'string' ? `Inside "${byId.get(folderId)?.name ?? ''}".` : 'At the top level.'}
        </p>
      </Modal>

      {/* Upload / new version */}
      <Modal open={uploadOpen} onClose={() => { setUploadOpen(false); setPendingFile(null); setReplacing(null) }}
        title={replacing ? `New version of "${replacing.title}"` : 'Upload a document'}
        footer={
          <>
            <button className={btnSecondary} onClick={() => { setUploadOpen(false); setPendingFile(null); setReplacing(null) }}>Cancel</button>
            <button className={btnPrimary} onClick={doUpload} disabled={upload.isPending || !pendingFile}>
              {upload.isPending ? 'Uploading…' : replacing ? `Upload v${replacing.version + 1}` : 'Upload'}
            </button>
          </>
        }>
        <div className="space-y-4">
          <DocumentUpload
            busy={upload.isPending}
            label={pendingFile ? `${pendingFile.name} · ${mb(pendingFile.size)}` : undefined}
            onFile={(f) => { setPendingFile(f); if (!title.trim()) setTitle(f.name.replace(/\.[^.]+$/, '')) }}
          />
          <Field label="Title"><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Anaphylaxis response" /></Field>
          {!replacing && (
            <Field label="Folder">
              <Select value={typeof folderId === 'string' ? folderId : ''} onChange={(e) => setFolderId(e.target.value || null)}>
                <option value="">Unfiled</option>
                {(folders.data ?? []).map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
              </Select>
            </Field>
          )}
          {replacing && (
            <p className="text-[12.5px] text-token-secondary">
              v{replacing.version} is kept and stays readable to admins. Crew see only the newest published version.
            </p>
          )}
          <p className="text-[12px] text-token-secondary">New documents start as a draft. Crew cannot see them until you publish.</p>
        </div>
      </Modal>

      {/* Version history */}
      <Modal open={!!historyFor} onClose={() => setHistoryFor(null)} title={historyFor ? `Versions of "${historyFor.title}"` : ''}
        footer={<button className={btnSecondary} onClick={() => setHistoryFor(null)}>Close</button>}>
        {versions.isLoading ? <SkeletonRows count={3} /> : (
          <ul className="divide-y divide-token-subtle">
            {(versions.data ?? []).map((v) => (
              <li key={v.id} className="flex items-center justify-between py-2.5">
                <span>
                  <span className="block text-[13.5px] font-medium text-token-primary">v{v.version} · {v.file_name}</span>
                  <span className="block text-[12px] text-token-secondary">{day(v.created_at)} · {mb(v.size_bytes)}</span>
                </span>
                <button className={btnSecondary + ' !h-8'} onClick={() => void open(v)}>Open</button>
              </li>
            ))}
          </ul>
        )}
      </Modal>
    </Page>
  )
}
